import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';

import { useIsMutating } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';

import { DASHBOARD_DEFAULT_BASE_NAME } from '@/entities/dashboard';
import { useDashboard } from '@/features/dashboard/api';
import { dashboardOptions } from '@/features/dashboard/api/dashboard.options';
import { DashboardStep } from '@/features/dashboard/constants/DashboardStep';
import { TAB_DEFAULT_BASE_NAME } from '@/features/dashboard/constants/names';
import { getUniqueDashboardName } from '@/features/dashboard/lib/getUniqueDashboardName';
import { getTabContent } from '@/features/dashboard/lib/transformers';
import { useDashboardEditLockContext } from '@/features/dashboard/state/DashboardEditLockContext';
import type { DashboardTab, MarketApps } from '@/features/dashboard/types';

import {
  decodeRouteParam,
  getFirstDashboardTabPath,
  resolveTabSelection
} from './useDashboardRouting.utils';

export const useDashboardRouting = () => {
  const [step, setStep] = useState(DashboardStep.Empty);
  const [dashboardConfig, setDashboardConfig] = useState<MarketApps | undefined>();
  const [dashboardStructure, setDashboardStructure] = useState<DashboardTab | undefined>();
  const [activeTab, setActiveTab] = useState(0);
  const [tabNames, setTabNames] = useState<string[]>([]);

  const { data: dashboardData, isLoading, isFetching } = useDashboard();
  const navigate = useNavigate();
  const params = useParams();
  const previousDashboardIdRef = useRef<string | undefined>(undefined);
  const knownDashboardNamesRef = useRef<Set<string>>(new Set());
  const { requestEditLock } = useDashboardEditLockContext();

  const draftDashboardName = useMemo(
    () =>
      params.dashboardId === 'new' && dashboardData
        ? getUniqueDashboardName(Object.keys(dashboardData))
        : null,
    [dashboardData, params.dashboardId]
  );

  const routeDashboardName = useMemo(() => {
    if (params.dashboardId === 'new') return draftDashboardName ?? DASHBOARD_DEFAULT_BASE_NAME;
    return params.dashboardId
      ? decodeURIComponent(params.dashboardId)
      : DASHBOARD_DEFAULT_BASE_NAME;
  }, [draftDashboardName, params.dashboardId]);
  const isDeletingRouteDashboard =
    useIsMutating({ mutationKey: dashboardOptions.mutations.delete(routeDashboardName) }) > 0;
  const isCreatingDashboard =
    useIsMutating({ mutationKey: dashboardOptions.mutations.create() }) > 0;
  const isSavingDashboard = useIsMutating({ mutationKey: dashboardOptions.mutations.save() }) > 0;
  const isUpdatingDashboard =
    useIsMutating({ mutationKey: dashboardOptions.mutations.updateAll() }) > 0;
  const isDashboardMutationReconciling =
    isDeletingRouteDashboard || isCreatingDashboard || isSavingDashboard || isUpdatingDashboard;

  const showNewDashboardDraft = useEffectEvent(() => {
    setTabNames([TAB_DEFAULT_BASE_NAME]);
    setActiveTab(0);
    setDashboardConfig([]);
    setDashboardStructure(undefined);
    setStep(DashboardStep.View);
  });

  // Main routing effect: resolves the current URL params to visible state.
  useEffect(() => {
    if (isLoading || !dashboardData) return;
    const dashboardParam = params.dashboardId ?? '';
    Object.keys(dashboardData).forEach(name => {
      knownDashboardNamesRef.current.add(name);
    });

    if (dashboardParam === 'new') {
      // eslint-disable-next-line react-hooks-js/set-state-in-effect
      showNewDashboardDraft();
      return;
    }

    if (!dashboardParam.trim()) {
      const path = getFirstDashboardTabPath(dashboardData);
      if (path) navigate(path, { replace: true });
      return;
    }

    const dashboardName = decodeRouteParam(dashboardParam);
    if (!(dashboardName in dashboardData)) {
      if (isFetching) return;
      if (isDashboardMutationReconciling) return;
      if (knownDashboardNamesRef.current.has(dashboardName)) {
        navigate(getFirstDashboardTabPath(dashboardData) ?? '/dashboard/new', { replace: true });
        return;
      }
      navigate('/notfound', { replace: true });
      return;
    }

    const dashboardTabs = dashboardData[dashboardName];
    const names = Object.keys(dashboardTabs);
    // eslint-disable-next-line react-hooks-js/set-state-in-effect
    setTabNames(names);
    // eslint-disable-next-line react-hooks-js/set-state-in-effect
    setDashboardStructure(dashboardTabs);

    const { tabIndex, redirectPath } = resolveTabSelection(
      dashboardName,
      names,
      decodeRouteParam(params.tabName)
    );
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
      return;
    }
    // eslint-disable-next-line react-hooks-js/set-state-in-effect
    setActiveTab(tabIndex);
    // eslint-disable-next-line react-hooks-js/set-state-in-effect
    setDashboardConfig(getTabContent(dashboardTabs[names[tabIndex]]));
  }, [
    dashboardData,
    navigate,
    params.dashboardId,
    params.tabName,
    isLoading,
    isFetching,
    isDashboardMutationReconciling
  ]);

  // Reset to View when navigating to a different dashboard while in Edit mode.
  const switchToView = useEffectEvent(() => {
    setStep(DashboardStep.View);
  });
  useEffect(() => {
    const prev = previousDashboardIdRef.current;
    const cur = params.dashboardId;
    if (
      step === DashboardStep.Edit &&
      !isSavingDashboard &&
      prev !== undefined &&
      prev !== 'new' &&
      cur !== prev
    ) {
      switchToView();
    }
    previousDashboardIdRef.current = cur;
  }, [params.dashboardId, step, isSavingDashboard]);

  const guardedSetStep = useCallback(
    (newStep: DashboardStep) => {
      if (newStep === DashboardStep.Edit) {
        requestEditLock(() => {
          setStep(DashboardStep.Edit);
        });
      } else {
        setStep(newStep);
      }
    },
    [requestEditLock]
  );

  const handleTabChange = useCallback(
    (tabIndex: number, tabName?: string) => {
      const name = tabName ?? tabNames[tabIndex];
      if (!name) return;
      setActiveTab(tabIndex);
      const tabs = dashboardStructure ?? dashboardData?.[routeDashboardName];
      if (tabs && name in tabs) setDashboardConfig(getTabContent(tabs[name]));
      navigate(`/dashboard/${encodeURIComponent(routeDashboardName)}/${encodeURIComponent(name)}`);
    },
    [navigate, tabNames, routeDashboardName, dashboardData, dashboardStructure]
  );

  return {
    step,
    setStep,
    guardedSetStep,
    dashboardConfig,
    dashboardStructure,
    activeTab,
    tabNames,
    handleTabChange,
    routeDashboardName,
    dashboardData,
    isLoading,
    isNewDashboard: params.dashboardId === 'new'
  };
};
